import { Text } from "@dropbox/dig-components/typography";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { dispatchElementClicked } from "@mirage/analytics/uxa/dispatchElementClicked";
import { selectedItemAtom } from "@mirage/mosaics/GlobalNav/KeyboardNavigation";
import { ResultItem } from "@mirage/mosaics/Results/ResultItem";
import ResultIcon from "@mirage/search/SearchResults/ResultIcon";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { DisplayType } from "@mirage/shared/search/search-result";
import { useAtom } from "jotai";
import { noop } from "lodash";
import { ResultSubtitle } from "../Metadata/ResultSubtitle";
import {
  getDisplayTypeForLogicalType,
  getResultConfigForDisplayType,
  getTitleConfig,
} from "../util/config";
import { getAttachments } from "../util/resultUtil";
import { ResultBody } from "./ResultBody";

import type { dash } from "@dropbox/api-v2-client";
import type { ContentIconProps } from "@dropbox/dash-component-library";
import type { LaunchMethod } from "@mirage/analytics/events/enums/launch_method";
import type {
  DisplayAction,
  ResultActionConfig,
} from "@mirage/search/SearchResults/ResultRow/types";
import type { SearchResult } from "@mirage/service-dbx-api";
import type { PersonObject } from "@mirage/service-people/types";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import type { TitleField } from "../util/config";

type ResultRowProps = {
  result: SearchResult;
  query: string;
  iconSize?: ContentIconProps["size"];
  onLaunch: DisplayAction["onClick"];
  resultActionConfig: ResultActionConfig;
  onOpenDropdown?: (isOpen: boolean) => void;
  onCopyResult: (result: SearchResult) => void;
  onShownResult?: (result: SearchResult) => void;
  resultPosition: number;
  annotationsEnabled?: boolean;
  peekResultActionsEnabled?: boolean;
  onClickPerson?: (person: PersonObject) => void;
  activeFilters?: SearchFilter[];
  animate?: boolean;
  delayMs?: number;
  noMargin?: boolean;
  displayOnly?: boolean;
  onAddToStack: (result: SearchResult) => void;
  onSummarize: (result: SearchResult) => void;
  verification?: dash.Curation;
};

export const ResultRow = ({
  result,
  query,
  onLaunch,
  resultActionConfig,
  onCopyResult,
  onShownResult,
  resultPosition,
  iconSize,
  annotationsEnabled,
  peekResultActionsEnabled,
  onClickPerson,
  activeFilters,
  animate = false,
  delayMs = 0,
  noMargin = false,
  displayOnly = false,
  onAddToStack,
  onSummarize,
  verification,
}: ResultRowProps) => {
  const [selectedItem, setSelectedItem] = useAtom(selectedItemAtom);
  const isDesktop = EnvCtx.surface === "desktop";
  const useConfigDisplayType = convertFeatureValueToBool(
    useFeatureFlagValue(
      "dash_2025_01_28_search_results_use_logical_type_config",
    ),
  );
  const isVirtualPathEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2024_11_13_search_results_virtual_path"),
  );
  const handleSelectItem = (selectionId: string) => {
    if (!displayOnly) {
      setSelectedItem({ selectionId });
    }
  };

  const displayType = useConfigDisplayType
    ? getDisplayTypeForLogicalType(result, isVirtualPathEnabled)
    : DisplayType.Knowledge;
  const displayConfig = getResultConfigForDisplayType(displayType);
  const titleSegments = displayConfig.title
    .map((titleField: TitleField) => getTitleConfig(titleField, result))
    .filter((titleSegment: string | null) => titleSegment !== null);

  return (
    <ResultItem
      resultUuid={result.uuid}
      query={query}
      titleSegments={titleSegments}
      selectionId={result.uuid}
      attachments={getAttachments(result)}
      debugUuid={result.uuid}
      onCopy={() => onCopyResult(result)}
      selected={
        !displayOnly ? selectedItem?.selectionId === result.uuid : false
      }
      icon={<ResultIcon result={result} size={iconSize} />}
      animate={animate}
      delayMs={delayMs}
      onShown={() => onShownResult?.(result)}
      subtext={
        <div>
          <Text
            size={isDesktop ? "small" : "medium"}
            color="subtle"
            variant="label"
          >
            <ResultSubtitle
              result={result}
              subtitleFields={displayConfig.subtitle}
              onClickPerson={onClickPerson}
              activeFilters={activeFilters}
            />
          </Text>
        </div>
      }
      bodyContent={
        <ResultBody
          result={result}
          query={query}
          bodyFields={displayConfig.body}
          resultPosition={resultPosition}
          annotationsEnabled={annotationsEnabled}
        />
      }
      onSelectItem={displayOnly ? noop : handleSelectItem}
      onLaunch={(launchMethod: LaunchMethod) => {
        if (displayOnly) {
          return;
        }

        // Don't trigger click if user is highlighting text
        if (!window?.getSelection()?.toString()) {
          // Manually trigger UXA event
          dispatchElementClicked(
            createUxaElementId("search_result_row", {
              actionSurfaceComponent: "result_row",
              featureLine: "search",
            }),
            result.uuid,
            result.analyticsTraceId,
          );

          onLaunch?.(launchMethod);
        }
      }}
      displayedActions={resultActionConfig.displayActions || []}
      peekResultActions={peekResultActionsEnabled}
      alignActionsTop={true}
      noMargin={noMargin}
      displayOnly={displayOnly}
      onAddToStack={displayOnly ? undefined : () => onAddToStack(result)}
      onSummarize={displayOnly ? undefined : () => onSummarize(result)}
      verification={verification}
    />
  );
};
